import { box, fitView, pointsAttr, project, shade, slab, withAlpha, type IsoView, type Point } from './iso';

export interface IsoCell {
  key: string;
  name: string;
  color: string;
  /** Valor que define a altura do bloco (percentual). */
  value: number;
  /** Texto exibido sobre o bloco. */
  label: string;
  /** Linha/coluna no gabarito da matriz. */
  row: number;
  col: number;
  /** Lado preferencial do callout externo. */
  side?: 'left' | 'right';
}

interface Props {
  cells: IsoCell[];
  rows: number;
  cols: number;
  /** Eixo desenhado na aresta inferior esquerda (varia com a linha). */
  axisLeft: string;
  /** Eixo desenhado na aresta inferior direita (varia com a coluna). */
  axisRight: string;
  width?: number;
  height?: number;
  /** Máximo usado na escala de altura (padrão: maior valor da série). */
  maxValue?: number;
  className?: string;
  /** Exibe o valor (percentual) também nos rótulos externos. */
  showExternalValues?: boolean;
  /** Exibe o texto interno sobre o topo dos blocos. */
  showCellLabels?: boolean;
  /** Largura do trilho lateral para rótulos externos (px). */
  railPadding?: number;
}

const CELL = 1.8;
const GAP = 0.14;
const PAD = 0.34;
const MAX_HEIGHT = 2.5;
const MIN_HEIGHT = 0.08;
const THICKNESS = 0.2;
/** Espaço lateral reservado aos rótulos externos. */
const RAIL = 126;
const LANE_SPACING = 30;

function luminance(hex: string): number {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16) / 255;
  const g = parseInt(value.slice(2, 4), 16) / 255;
  const b = parseInt(value.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Infográfico isométrico: blocos extrudados sobre plataforma, com linhas-guia
 * e rótulos externos. Totalmente vetorial (SVG), fiel na tela e no PDF.
 */
export function IsoBlockChart({
  cells,
  rows,
  cols,
  axisLeft,
  axisRight,
  width = 1000,
  height = 620,
  maxValue,
  className,
  showExternalValues = false,
  showCellLabels = true,
  railPadding,
}: Props): JSX.Element {
  const gridWidth = rows * CELL + (rows - 1) * GAP + PAD * 2;
  const gridDepth = cols * CELL + (cols - 1) * GAP + PAD * 2;
  const view: IsoView = fitView({
    svgWidth: width,
    svgHeight: height,
    gridWidth,
    gridDepth,
    maxHeight: MAX_HEIGHT,
    thickness: THICKNESS,
    paddingX: railPadding ?? RAIL,
    paddingY: 42,
  });

  const platform = slab(gridWidth, gridDepth, THICKNESS, view);
  const scaleMax = Math.max(maxValue ?? Math.max(...cells.map((cell) => cell.value), 0), 1);

  // x = linha (eixo esquerdo), y = coluna invertida (eixo direito): coluna maior = mais ao fundo.
  const geometry = cells
    .map((cell) => {
      const xi = cell.row;
      const yi = cols - 1 - cell.col;
      const x0 = PAD + xi * (CELL + GAP);
      const y0 = PAD + yi * (CELL + GAP);
      const h = cell.value <= 0 ? MIN_HEIGHT : MIN_HEIGHT + (cell.value / scaleMax) * MAX_HEIGHT;
      return { cell, faces: box(x0, x0 + CELL, y0, y0 + CELL, h, view), depth: xi + yi, xi, yi, h };
    })
    .sort((a, b) => a.depth - b.depth);

  const leftCorner = project(0, gridDepth, 0, view);
  const frontCorner = project(gridWidth, gridDepth, -THICKNESS, view);
  const rightCorner = project(gridWidth, 0, 0, view);

  const axisLeftMid: Point = [
    (leftCorner[0] + frontCorner[0]) / 2 - 30,
    (leftCorner[1] + frontCorner[1]) / 2 + 40,
  ];
  const axisRightMid: Point = [
    (frontCorner[0] + rightCorner[0]) / 2 + 30,
    (frontCorner[1] + rightCorner[1]) / 2 + 40,
  ];

  // Rótulos externos em trilhos laterais, sem sobreposição: cada lado recebe
  // faixas verticais sequenciais a partir do bloco mais alto.
  const sceneCenterX = (leftCorner[0] + rightCorner[0]) / 2;
  const callouts = geometry
    .map(({ cell, faces }) => ({
      cell,
      anchor: [faces.topCenter[0], faces.topCenter[1] - 6] as Point,
      side: cell.side ?? ((faces.topCenter[0] < sceneCenterX ? 'left' : 'right') as 'left' | 'right'),
    }))
    .sort((a, b) => a.anchor[1] - b.anchor[1]);

  const nextLane: Record<'left' | 'right', number> = { left: 26, right: 26 };
  const labels = callouts.map((callout) => {
    const laneY = Math.max(nextLane[callout.side], callout.anchor[1] - 46);
    nextLane[callout.side] = laneY + LANE_SPACING;
    const usedRail = railPadding ?? RAIL;
    const dotX = callout.side === 'left' ? usedRail - 18 : width - usedRail + 18;
    return { ...callout, laneY, dotX };
  });

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      role="img"
      aria-label={`Gráfico isométrico ${axisLeft} por ${axisRight}`}
    >
      <defs>
        <linearGradient id="iso-slab" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F2F2F5" />
          <stop offset="100%" stopColor="#E3E3E8" />
        </linearGradient>
        <filter id="iso-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="10" stdDeviation="12" floodColor="#1B1A20" floodOpacity="0.16" />
        </filter>
      </defs>

      {/* Plataforma */}
      <g filter="url(#iso-shadow)">
        <polygon points={pointsAttr(platform.left)} fill="#CFCFD6" />
        <polygon points={pointsAttr(platform.right)} fill="#BFBFC7" />
        <polygon points={pointsAttr(platform.top)} fill="url(#iso-slab)" />
      </g>

      {/* Marcações dos eixos sobre a plataforma */}
      <g fontSize="15" fontWeight="700" fill="#8E8C99" fontFamily="inherit">
        <text x={leftCorner[0] + 30} y={leftCorner[1] - 6} textAnchor="middle">
          (+)
        </text>
        <text x={frontCorner[0] - 24} y={frontCorner[1] - 26} textAnchor="middle">
          (–)
        </text>
        <text x={frontCorner[0] + 24} y={frontCorner[1] - 26} textAnchor="middle">
          (–)
        </text>
        <text x={rightCorner[0] - 30} y={rightCorner[1] - 6} textAnchor="middle">
          (+)
        </text>
      </g>

      {/* Blocos */}
      {geometry.map(({ cell, faces }) => {
        const empty = cell.value <= 0;
        const base = cell.color;
        const textColor = luminance(base) > 0.62 ? '#2F2E36' : '#FFFFFF';
        return (
          <g key={cell.key}>
            <polygon points={pointsAttr(faces.left)} fill={shade(base, 0.74)} />
            <polygon points={pointsAttr(faces.right)} fill={shade(base, 0.56)} />
            <polygon points={pointsAttr(faces.top)} fill={base} stroke={withAlpha('#FFFFFF', 0.35)} strokeWidth="1" />
            {!empty && showCellLabels && (
              <text
                x={faces.topCenter[0]}
                y={faces.topCenter[1] + 9}
                textAnchor="middle"
                fontSize="26"
                fontWeight="700"
                fill={textColor}
                fontFamily="inherit"
              >
                {cell.label}
              </text>
            )}
          </g>
        );
      })}

      {/* Linhas-guia e rótulos dos grupos */}
      {labels.map(({ cell, anchor, side, laneY, dotX }) => {
        const empty = cell.value <= 0;
        const color = cell.color;
        return (
          <g key={`label-${cell.key}`}>
            <polyline
              points={pointsAttr([
                [anchor[0], anchor[1]],
                [anchor[0], laneY],
                [dotX, laneY],
              ])}
              fill="none"
              stroke={color}
              strokeWidth="1.4"
              strokeLinecap="round"
            />
            <circle cx={dotX} cy={laneY} r="4.5" fill={color} />
            <text
              x={side === 'left' ? dotX - 10 : dotX + 10}
              y={laneY + 5}
              textAnchor={side === 'left' ? 'end' : 'start'}
              fontSize="15"
              fontWeight="700"
              fill="#2F2E36"
              fontFamily="inherit"
            >
              {cell.name}
            </text>
            {showExternalValues && !empty && (
              <text
                x={side === 'left' ? dotX - 10 : dotX + 10}
                y={laneY + 20}
                textAnchor={side === 'left' ? 'end' : 'start'}
                fontSize="11"
                fontWeight="700"
                className="tabular-nums"
                fill="#5F5D6B"
                fontFamily="inherit"
              >
                {cell.label}
              </text>
            )}
            {empty && (
              <text
                x={side === 'left' ? dotX - 10 : dotX + 10}
                y={laneY + 20}
                textAnchor={side === 'left' ? 'end' : 'start'}
                fontSize="11"
                fill="#8E8C99"
                fontFamily="inherit"
              >
                0%
              </text>
            )}
          </g>
        );
      })}

      {/* Rótulos dos eixos */}
      <g fontFamily="inherit" fill="#8E8C99" fontSize="17" fontWeight="700" letterSpacing="1.5">
        <text
          x={axisLeftMid[0]}
          y={axisLeftMid[1]}
          textAnchor="middle"
          transform={`rotate(30 ${axisLeftMid[0]} ${axisLeftMid[1]})`}
        >
          {`\u2190  ${axisLeft.toUpperCase()}`}
        </text>
        <text
          x={axisRightMid[0]}
          y={axisRightMid[1]}
          textAnchor="middle"
          transform={`rotate(-30 ${axisRightMid[0]} ${axisRightMid[1]})`}
        >
          {`${axisRight.toUpperCase()}  \u2192`}
        </text>
      </g>
    </svg>
  );
}
